import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { AllergenBadges } from "@/components/AllergenBadges";
import { resolveImageUrl } from "@/components/ImageUpload";
import { formatQty } from "@/lib/locale";
import { Utensils, GlassWater, Loader2, Clock, Users } from "lucide-react";

// Public read-only recipe page. No auth, no cost data: safe to share.
export default function PublicRecipe() {
  const { id } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Bare axios so we don't send auth cookies unnecessarily.
        const { data } = await axios.get(`${API}/public/recipes/${id}`);
        if (!cancelled) setRecipe(data);
      } catch (err) {
        if (!cancelled) setNotFound(err?.response?.status === 404);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (notFound || !recipe) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 px-6" data-testid="public-recipe-notfound">
        <div className="label-eyebrow">Recipe</div>
        <h1 className="font-display text-3xl">Not found</h1>
        <p className="text-sm text-muted-foreground">This recipe may have been removed or the link is incorrect.</p>
        <Link to="/" className="text-primary hover:underline text-sm">Return home</Link>
      </div>
    );
  }

  const isDrink = recipe.bracket === "drink";
  const prepMinutes = Number(recipe.prep_time_minutes);
  const prepLabel = Number.isFinite(prepMinutes) && prepMinutes > 0
    ? (prepMinutes >= 60 ? `${(prepMinutes / 60).toFixed(prepMinutes % 60 === 0 ? 0 : 1)}h` : `${prepMinutes}m`)
    : null;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="public-recipe-page">
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-8 md:py-12">
        <header className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-2 font-display text-lg tracking-tight">
            <span className="w-7 h-7 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold">C</span>
            CulinaireSpecialist
          </Link>
          <span className="label-eyebrow hidden sm:block">Shared recipe</span>
        </header>

        <div className="rounded-3xl overflow-hidden glass-card">
          {recipe.image_url ? (
            <img
              src={resolveImageUrl(recipe.image_url)}
              alt={recipe.name}
              className="w-full h-64 md:h-[420px] object-cover"
              data-testid="public-recipe-image"
            />
          ) : (
            <div className="w-full h-64 md:h-[420px] bg-secondary flex items-center justify-center text-muted-foreground/40">
              {isDrink ? <GlassWater className="w-20 h-20" /> : <Utensils className="w-20 h-20" />}
            </div>
          )}
          <div className="p-4 sm:p-6 lg:p-10 space-y-6">
            <div>
              <div className="label-eyebrow capitalize">{recipe.bracket}</div>
              <h1 className="font-display text-4xl md:text-6xl leading-tight mt-2" data-testid="public-recipe-name">{recipe.name}</h1>
              {recipe.description && (
                <p className="text-muted-foreground mt-4 text-base md:text-lg max-w-3xl leading-relaxed">
                  {recipe.description}
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-6 pt-2 border-t border-border/60">
              {recipe.servings > 0 && (
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <div className="label-eyebrow text-[0.55rem]">Servings</div>
                    <div className="font-display text-lg tabular">{recipe.servings}</div>
                  </div>
                </div>
              )}
              {prepLabel && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <div className="label-eyebrow text-[0.55rem]">Prep time</div>
                    <div className="font-display text-lg tabular">{prepLabel}</div>
                  </div>
                </div>
              )}
              {recipe.allergens?.length > 0 && (
                <div className="flex-1 min-w-[200px]">
                  <div className="label-eyebrow text-[0.55rem] mb-1">Allergens</div>
                  <AllergenBadges allergens={recipe.allergens} testId="public-recipe-allergens" />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-5 gap-6 md:gap-10 mt-8">
          <section className="md:col-span-2 rounded-2xl glass-card p-6" data-testid="public-recipe-ingredients">
            <h2 className="font-display text-2xl mb-4">Ingredients</h2>
            {recipe.ingredients?.length ? (
              <ul className="space-y-3">
                {recipe.ingredients.map((line, i) => (
                  <li key={`${line.name}-${i}`} className="flex items-baseline justify-between gap-3 border-b border-border/40 pb-2 last:border-none">
                    <span className="text-sm">{line.name}</span>
                    <span className="text-sm tabular text-muted-foreground whitespace-nowrap">
                      {formatQty(line.quantity, line.unit)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No ingredients listed.</p>
            )}
          </section>

          <section className="md:col-span-3 rounded-2xl glass-card p-6" data-testid="public-recipe-method">
            <h2 className="font-display text-2xl mb-4">Method</h2>
            {recipe.method?.length ? (
              <ol className="space-y-4">
                {recipe.method.map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="w-7 h-7 shrink-0 rounded-full bg-primary/15 text-primary font-semibold text-xs flex items-center justify-center tabular">
                      {i + 1}
                    </span>
                    <p className="text-sm leading-relaxed flex-1 pt-1">{step}</p>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-muted-foreground">Method not shared for this recipe.</p>
            )}
          </section>
        </div>

        <footer className="mt-12 pt-6 border-t border-border/40 text-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid="public-recipe-footer-link"
          >
            <span className="w-4 h-4 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[0.55rem] font-bold">C</span>
            Powered by <span className="font-display text-foreground">CulinaireSpecialist</span>: precision costing for restaurants
          </a>
        </footer>
      </div>
    </div>
  );
}
